import { Injectable, Inject, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError, asyncScheduler, Subject } from 'rxjs';
import { tap, catchError, shareReplay, finalize, observeOn } from 'rxjs/operators';
import { Matter } from '../api/data-model';
import { BACKEND_URL } from '../app.config';

@Injectable({
  providedIn: 'root'
})
export class ShareDataService {
  private ongoingRequests = new Map<string, Observable<Matter>>();
  private matterSubject = new BehaviorSubject<Matter | null>(null);
  public matter$ = this.matterSubject.asObservable();

  private lastBreadcrumbSubject = new BehaviorSubject<string | null>(null);
  public lastBreadcrumb$ = this.lastBreadcrumbSubject.asObservable();

  private relatedMatterUpdatedSubject = new Subject<void>();
  public relatedMatterUpdated$ = this.relatedMatterUpdatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(BACKEND_URL) private baseUrl: string,
    private router: Router,
    private ngZone: NgZone
  ) {}

  getMatterByCode(code: string): Observable<Matter> {
  if (this.ongoingRequests.has(code)) {
    return this.ongoingRequests.get(code)!;
  }

  const request$ = this.ngZone.run(() => {  // Ensure HTTP request runs inside Angular zone
    return this.http.get<Matter>(`${this.baseUrl}/api/app/matter/by-code?code=${code}`)
      .pipe(
        tap(matter => {
          this.matterSubject.next(matter);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.router.navigate(['/notfound'], { queryParams: { code } });
            return new Observable<Matter>(() => {});
          }
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        finalize(() => {
          this.ongoingRequests.delete(code);
        })
      );
  });

  this.ongoingRequests.set(code, request$);
  return request$;
}

  getCurrentMatter(): Matter | null {
    return this.matterSubject.value;
  }

  setLastBreadcrumb(name: string | null): void {
    this.lastBreadcrumbSubject.next(name);
  }

  clearLastBreadcrumb(): void {
    this.lastBreadcrumbSubject.next(null);
  }

  notifyRelatedMatterUpdated(): void {
    this.relatedMatterUpdatedSubject.next();
  }
}
